/** Module storing commony used Suspension constants and methods */

import LoggerService from "@bfx/logger";
import { OrderData, StrategyParameter } from "shared/cubiq-model-multi-asset";
import { SuspensionAdapter } from "shared/cubiq-services/adapter/orderBlotter/SuspensionAdapter";

export const VALID_FROM_TIME: string = "ValidFromTime";
export const VALID_TO_TIME: string = "ValidToTime";
export const VALID_FROM_TIMEZONE: string = "ValidFromTZ";
export const VALID_TO_TIMEZONE: string = "ValidToTZ";

export const SUSPENSION_PARAM_NAME: Array<string> = [
  VALID_FROM_TIME,
  VALID_TO_TIME,
  VALID_FROM_TIMEZONE,
  VALID_TO_TIMEZONE,
];
export const SUSPENSION_PARAM_TYPE: number = 14;

/**
 * Type describing Suspension Details as represented in entry convention
 */
export type SuspensionDetails = {
  fromTime: string;
  fromTimeTZ: string;
  toTime: string;
  toTimeTZ: string;
};

const logger = LoggerService.for(__dirname);

const EMPTY_DATE = "-";
/**
 *  Utility class for extracting suspension details and descriptions
 */
export class SuspensionHelper {
  /**
   * Get text description for the suspension details of the order
   * @param orderData
   * @param asLocalTime Should times be converted to local times (default is false)
   * @returns Formatted suspension string or "-" if no suspension found
   */
  public getSuspensionDescription(orderData: OrderData, asLocalTime: boolean = false): string {
    const suspensionDetails = asLocalTime
      ? this.getLocalSuspensionDetails(orderData)
      : this.getEntrySuspensionDetails(orderData);

    if (suspensionDetails) {
      return `From ${suspensionDetails.fromTime} ${suspensionDetails.fromTimeTZ} to ${suspensionDetails.toTime} ${suspensionDetails.toTimeTZ}`;
    }
    return EMPTY_DATE;
  }

  /**
   * Use the times  from suspensionDetails to represent all times in local timezone
   * @param orderData
   * @returns
   */
  private getLocalSuspensionDetails(orderData: OrderData) {
    const suspensionAdapter = new SuspensionAdapter();
    let suspendFrom = suspensionAdapter.retrieveData(orderData, "suspensionFromDts");
    let suspendTo = suspensionAdapter.retrieveData(orderData, "suspensionToDts");

    if (suspendFrom !== EMPTY_DATE && suspendTo !== EMPTY_DATE) {
      return { fromTime: suspendFrom, fromTimeTZ: "", toTime: suspendTo, toTimeTZ: "" };
    }
  }

  /**
   * Extract the full SuspensionDetails from the OrderData (currently using the strategy parameters)
   * @param orderData
   * @returns  suspension details or null if no details OR an error was found
   */
  public getEntrySuspensionDetails(orderData: OrderData): SuspensionDetails {
    if (orderData.suspensionDetails != null && orderData.strategyParameters != null) {
      let parameters: Array<StrategyParameter> = orderData.strategyParameters.filter(
        (item) =>
          item.parameterType === SUSPENSION_PARAM_TYPE &&
          SUSPENSION_PARAM_NAME.includes(item.parameterName),
      );

      if (parameters.length === 4) {
        let fromTime = this.getParameterValue(parameters, VALID_FROM_TIME);
        let fromTimeTZ = this.getParameterValue(parameters, VALID_FROM_TIMEZONE);
        let toTime = this.getParameterValue(parameters, VALID_TO_TIME);
        let toTimeTZ = this.getParameterValue(parameters, VALID_TO_TIMEZONE);

        fromTime = this.formatTime(fromTime);
        toTime = this.formatTime(toTime);

        if (fromTime && toTime && fromTimeTZ && toTimeTZ) {
          return { fromTime, fromTimeTZ, toTime, toTimeTZ };
        } else {
          logger.out.error(
            `ERROR: .getSuspensionDetails(): Invalid set of suspension parameters size ${parameters.length}`,
          );
        }
      }
    }

    return null;
  }

  /**
   * Get the parameter values assoicated with the parameter name from the array of StrategyParameter
   * @param parameters
   * @param name
   * @returns
   */
  private getParameterValue(parameters: StrategyParameter[], name: string): string {
    let value = parameters.find((item: StrategyParameter) => item.parameterName === name);
    if (value) {
      return value.parameterValue;
    }
    return null;
  }

  /**
   * Ensure time string is correct format for display and adding to a Time control. Currently supports
   * 'HHMM' and 'HH:MM' formats - all others with return the input string,
   * @param timeStr
   * @returns
   */
  private formatTime(timeStr: string): string {
    if (timeStr && timeStr.indexOf(":") == -1 && timeStr.length == 4) {
      return timeStr.slice(0, 2) + ":" + timeStr.slice(2);
    }
    return timeStr;
  }
}
